import React from "react";

import { faImage } from "@fortawesome/pro-duotone-svg-icons/faImage";
import { faPalette } from "@fortawesome/pro-solid-svg-icons/faPalette";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { ColorDropdown } from "@mapmycustomers/ui";

import Preset, {
  findPreset,
  getColors,
} from "@app/scene/dashboard/components/Header/components/Gradient/Preset";

import MenuOption from "./MenuOption";
import styles from "./PresetsMenu.module.scss";

interface Props {
  auto?: boolean;
  colorPickerVisible?: boolean;
  colors?: AnyColor[];
  onChangeAuto?: (auto: boolean) => void;
  onChangeColorPickerVisibility?: (visible: boolean) => void;
  onChangeColors?: (colors: AnyColor[]) => void;
}

const PresetsMenu: React.FC<Props> = ({
  auto,
  colorPickerVisible,
  colors,
  onChangeAuto,
  onChangeColorPickerVisibility,
  onChangeColors,
}) => {
  const intl = useIntl();
  const activePreset = findPreset(colors);

  const handleColorChange = useDebouncedCallback(
    [
      (color: AnyColor) => {
        onChangeColors?.([color]);
      },
      1000,
    ],
    [onChangeColors]
  );

  return (
    <div className={styles.container}>
      <Tooltip
        placement="left"
        title={intl.formatMessage({
          id: "dashboard.header.gradient.edit.auto",
          defaultMessage: "Derive colors from header logo",
          description:
            "Tooltip of the Auto option in presets list in edit gradient popover on the Dashboard page",
        })}
      >
        <MenuOption key="auto" onClick={() => onChangeAuto?.(!auto)} selected={auto}>
          <FontAwesomeIcon className={cn({ [styles.autoSelected]: auto })} icon={faImage} />
        </MenuOption>
      </Tooltip>
      {(Object.values(Preset) as Preset[]).map((preset) => (
        <MenuOption
          key={preset}
          onClick={() => onChangeColors?.(getColors(preset))}
          selected={preset === activePreset && !auto}
          style={{
            backgroundImage: `linear-gradient(to bottom, ${getColors(preset).join(", ")})`,
          }}
        />
      ))}
      <ColorDropdown
        colorPanelProps={{
          showColorSpace: true,
          showInput: true,
          showNamedColors: true,
          showOpacitySelector: false,
        }}
        onChange={handleColorChange}
        onOpenChange={onChangeColorPickerVisibility}
        open={colorPickerVisible}
        trigger={["hover"]}
        value={!activePreset && colors?.length ? colors[0] : undefined}
      >
        <MenuOption key="custom" selected={!activePreset}>
          <FontAwesomeIcon className={styles.customIcon} icon={faPalette} />
        </MenuOption>
      </ColorDropdown>
    </div>
  );
};

export default PresetsMenu;
